import React, { useState } from "react";
import { Mail, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import Reveal from "../components/site/Reveal";
import { api } from "../lib/api";

const PLATFORMS = ["SAP BRIM", "Oracle Fusion", "Salesforce Revenue Cloud", "Not sure yet"];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    platform: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.message || form.message.trim().length < 10) e.message = "Tell us a bit more (10+ characters).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (ev) => setForm((f) => ({ ...f, [k]: ev.target.value }));

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        platform: form.platform || null,
        message: form.message.trim(),
      });
      setSuccess(true);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", phone: "", platform: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : detail || "Something went wrong.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Toaster theme="dark" position="top-right" richColors />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-10 md:pb-12 border-b border-white/5" data-testid="contact-hero">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="container-xp relative">
          <div className="eyebrow-lg mb-5">Contact</div>
          <h1 className="font-display tracking-display text-[40px] md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl" data-testid="contact-title">
            Tell us about <br /> your revenue engine.
          </h1>
          <p className="mt-8 text-xp-muted max-w-2xl">
            Whether you're scoping a full BRIM rollout or just want a second opinion on your Oracle or Salesforce roadmap —
            we'll get back within one business day.
          </p>
        </div>
      </section>

      {/* Form + Info */}
      <section className="section-xp">
        <div className="container-xp grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="border border-white/10 rounded-sm p-6 md:p-10 bg-xp-surface/60 space-y-6"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid md:grid-cols-2 gap-6">
                <Field
                  label="Your name"
                  id="name"
                  value={form.name}
                  onChange={onChange("name")}
                  error={errors.name}
                  required
                  placeholder="Alex Martins"
                  testid="contact-input-name"
                />
                <Field
                  label="Work email"
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  error={errors.email}
                  required
                  placeholder="alex@company.com"
                  testid="contact-input-email"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Field
                  label="Company"
                  id="company"
                  value={form.company}
                  onChange={onChange("company")}
                  placeholder="Company name"
                  testid="contact-input-company"
                />
                <Field
                  label="Phone (optional)"
                  id="phone"
                  value={form.phone}
                  onChange={onChange("phone")}
                  placeholder="+1 …"
                  testid="contact-input-phone"
                />
              </div>

              <div>
                <label className="eyebrow block mb-3" htmlFor="platform">Platform interest</label>
                <div className="flex flex-wrap gap-2" data-testid="contact-platform-chips">
                  {PLATFORMS.map((p) => {
                    const active = form.platform === p;
                    return (
                      <button
                        type="button"
                        key={p}
                        onClick={() => setForm((f) => ({ ...f, platform: active ? "" : p }))}
                        data-testid={`contact-platform-${p.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`px-3 py-2 rounded-sm text-xs font-mono uppercase tracking-widest border transition-all ${
                          active ? "bg-xp-cyan text-xp-bg border-xp-cyan" : "border-white/10 text-xp-muted hover:text-xp-text hover:border-white/25"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="eyebrow block mb-3" htmlFor="message">How can we help?<span className="text-red-400 ml-1">*</span></label>
                <textarea
                  id="message"
                  value={form.message}
                  onChange={onChange("message")}
                  rows={5}
                  placeholder="A few lines about your current stack, challenges, and timeline…"
                  data-testid="contact-input-message"
                  className={`w-full bg-xp-bg border rounded-sm px-4 py-3 text-sm text-xp-text placeholder:text-xp-dim focus:outline-none focus:border-xp-cyan transition-colors ${
                    errors.message ? "border-red-500/60" : "border-white/10"
                  }`}
                />
                {errors.message && <p className="mt-2 text-xs text-red-400 font-mono" data-testid="error-message">{errors.message}</p>}
              </div>

              <div className="flex items-center justify-between pt-2 flex-wrap gap-4">
                <p className="text-xs text-xp-dim font-mono">We'll reply within 1 business day.</p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-cyan disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {submitting ? "Sending…" : "Send message"}
                </button>
              </div>

              {success && (
                <div className="flex items-start gap-3 border border-xp-cyan/40 bg-xp-cyan/5 p-4 rounded-sm" data-testid="contact-success">
                  <CheckCircle2 className="h-4 w-4 text-xp-cyan mt-0.5" />
                  <div className="text-sm">
                    Thanks — we received your message and will be in touch within one business day.
                  </div>
                </div>
              )}
            </form>
          </Reveal>

          {/* Info */}
          <Reveal delay={0.1} className="lg:col-span-5 space-y-8">
            <div className="border border-white/10 rounded-sm p-8 bg-xp-surface/60" data-testid="contact-info-card">
              <div className="eyebrow mb-5">Direct lines</div>
              <div className="space-y-5">
                <InfoLine Icon={Mail} label="Email" value="HR@xpace.work" />
              </div>
            </div>

            <OfficeCard />

            <div className="border border-white/10 rounded-sm p-8">
              <div className="eyebrow mb-4">Response SLA</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="font-display text-2xl text-xp-cyan">&lt; 1 day</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-xp-dim mt-1">Initial reply</div>
                </div>
                <div>
                  <div className="font-display text-2xl text-xp-cyan">&lt; 1 week</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-xp-dim mt-1">Discovery call</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, id, type = "text", value, onChange, error, required, placeholder, testid }) => (
  <div>
    <label className="eyebrow block mb-3" htmlFor={id}>
      {label}
      {required && <span className="text-red-400 ml-1">*</span>}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className={`w-full bg-xp-bg border rounded-sm px-4 py-3 text-sm text-xp-text placeholder:text-xp-dim focus:outline-none focus:border-xp-cyan transition-colors ${
        error ? "border-red-500/60" : "border-white/10"
      }`}
    />
    {error && <p className="mt-2 text-xs text-red-400 font-mono" data-testid={`error-${id}`}>{error}</p>}
  </div>
);

const InfoLine = ({ Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="h-9 w-9 flex items-center justify-center border border-white/10 rounded-sm bg-xp-bg">
      <Icon className="h-4 w-4 text-xp-cyan" />
    </div>
    <div>
      <div className="eyebrow">{label}</div>
      <div className="text-sm text-xp-text mt-0.5">{value}</div>
    </div>
  </div>
);

const OFFICES = {
  usa: {
    code: "USA",
    flag: "🇺🇸",
    city: "Sacramento",
    address: ["1401 21st St, Ste R", "Sacramento, CA 95811", "United States"],
    timezone: "PST · UTC-8",
    hours: "Mon–Fri · 9:00 – 18:00",
  },
  india: {
    code: "INDIA",
    flag: "🇮🇳",
    city: "Hyderabad",
    address: ["Ste 8, Sushie Eden Garden", "Kokapet, Hyderabad 500075", "India"],
    timezone: "IST · UTC+5:30",
    hours: "Mon–Fri · 9:30 – 18:30",
  },
};

const OfficeCard = () => {
  const [active, setActive] = React.useState("usa");
  const o = OFFICES[active];
  return (
    <div className="border border-white/10 rounded-sm bg-xp-surface/60 overflow-hidden" data-testid="office-card">
      <div className="grid grid-cols-2" role="tablist">
        {Object.entries(OFFICES).map(([key, v]) => {
          const isActive = active === key;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(key)}
              data-testid={`office-tab-${key}`}
              className={`px-6 py-5 text-left transition-all border-b-2 ${
                isActive
                  ? "bg-xp-bg border-xp-cyan"
                  : "bg-xp-surface/40 border-transparent hover:bg-xp-surface"
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-xl leading-none" aria-hidden>{v.flag}</span>
                <div>
                  <div className={`font-mono text-[10px] tracking-widest uppercase ${isActive ? "text-xp-cyan" : "text-xp-dim"}`}>
                    {v.code} office
                  </div>
                  <div className={`font-display text-base mt-1 tracking-display ${isActive ? "text-xp-text" : "text-xp-muted"}`}>
                    {v.city}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div key={active} className="p-8 space-y-6 animate-in fade-in duration-300" data-testid={`office-panel-${active}`}>
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 flex items-center justify-center border border-white/10 rounded-sm bg-xp-bg flex-shrink-0">
            <MapPin className="h-4 w-4 text-xp-cyan" />
          </div>
          <div>
            <div className="eyebrow">Address</div>
            <div className="text-sm text-xp-text mt-1 leading-relaxed">
              {o.address.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-white/5">
          <div>
            <div className="eyebrow">Timezone</div>
            <div className="text-sm text-xp-text mt-1 font-mono">{o.timezone}</div>
          </div>
          <div>
            <div className="eyebrow">Hours</div>
            <div className="text-sm text-xp-text mt-1">{o.hours}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
